// evaluationNatConfig.js
//
// Les tests spécifiques (fichier « questions », phase 2 de l'import) : `expectedQuestions` liste, pour
// chaque niveau, les tests de L'ANNÉE EN COURS (décision du 09/10/2026 : on change année par année).
// L'import cherche leurs colonnes « <entete> Q01 - réponse » / « <entete> Q01 - score » dans TOUTES les
// feuilles, quelles qu'elles soient, et bloque un test inconnu, absent, incomplet ou en double.
//   - displayName : le nom sous lequel les réponses sont rangées (celui que l'affichage lit) ;
//   - entete      : le nom du test tel que l'écrit la plateforme nationale, s'il diffère de displayName ;
//   - count       : le nombre d'items.
// CHAQUE ANNÉE : relire les en-têtes d'un fichier exporté, mettre à jour expectedQuestions, ajouter les
// nouveaux textes de compréhension à TEXTES_COMPREHENSION (sans retirer les anciens : l'affichage des
// années passées en a besoin), et faire saisir côté back les questions nationales des nouveaux textes.

/** Les textes de compréhension de l'écrit, d'hier et d'aujourd'hui : nom court (clé des réponses) et
 * type_label des questions nationales en base. L'affichage les connaît tous ; l'import ne prend que ceux
 * de l'année (expectedQuestions). */
export const TEXTES_COMPREHENSION = [
  { nom: 'Arachné', typeLabel: "Compréhension de l'écrit Arachné" },
  { nom: 'Sommeil', typeLabel: "Compréhension de l'écrit Document Sommeil" },
  { nom: 'Petit Chose', typeLabel: "Compréhension de l'écrit Petit Chose" },
  { nom: 'Gaspillage', typeLabel: "Compréhension de l'écrit Gaspillage" },
  { nom: 'Volcans sous la glace', typeLabel: "Compréhension de l'écrit Volcans sous la glace" },
];

/** Le nom court d'un texte depuis le type_label d'une question nationale
 * (« Compréhension de l'écrit Document Sommeil » → « Sommeil »). */
export const nomDuTexte = (typeLabel) => {
  const connu = TEXTES_COMPREHENSION.find((t) => t.typeLabel === typeLabel);
  if (connu) return connu.nom;
  const nom = String(typeLabel || '').replace("Compréhension de l'écrit ", '');
  return nom.startsWith('Document ') ? nom.replace('Document ', '') : nom;
};

/** Le rang d'un texte dans TEXTES_COMPREHENSION (pour lister texte par texte), 0 hors de la liste. */
export const rangDuTexte = (nom) => Math.max(0, TEXTES_COMPREHENSION.findIndex((t) => t.nom === nom));

export const EVAL_NAT_CONFIG = {
  maths: {
    "6": {
      columns: {
        automatisme: {
          patterns: ['test', 'specifique', 'automatisme'],
          exclude: ['score', /q\d+/i],
          outputName: 'Automatisme',
          displayName: 'Niveau de maîtrise - Automatisme'
        },
        probleme: {
          patterns: ['test', 'specifique', 'probleme', 'resolution'], // OR condition
          exclude: ['score', /q\d+/i],
          outputName: 'Probleme',
          displayName: 'Niveau de maîtrise - Résolution de problèmes'
        },
        espaceGeometrie: {
          patterns: ['espace', 'geometrie'], // AND condition (both words)
          exclude: ['score', /q\d+/i],
          outputName: 'EEG',
          displayName: 'Espace et Géométrie'
        },
        grandeursMesures: {
          patterns: ['grandeur', 'mesure'], // AND condition
          exclude: ['score', /q\d+/i],
          outputName: 'GEM',
          displayName: 'Grandeurs et Mesures'
        },
        nombresCalculs: {
          patterns: ['nombre', 'calcul'], // AND condition
          exclude: ['score', /q\d+/i],
          outputName: 'NEC',
          displayName: 'Nombres et Calculs'
        }
      },
      expectedQuestions: {
        automatismes: { count: 23, displayName: 'Automatismes' },
        problemes: { count: 19, displayName: 'Problèmes' }
      }
    },

    "5": {
      columns: {
        automatisme: {
          patterns: ['test', 'specifique', 'automatisme'],
          exclude: ['score', /q\d+/i],
          outputName: 'Automatisme',
          displayName: 'Test spécifique automatisme'
        }
      },
      expectedQuestions: {
        automatismes: { count: 30, displayName: 'Automatismes' }
      }
    },

    "4": {
      columns: {
        // Reprend toutes les colonnes de 6ème
        automatisme: {
          patterns: ['test', 'specifique', 'automatisme'],
          exclude: ['score', /q\d+/i],
          outputName: 'Automatisme',
          displayName: 'Niveau de maîtrise - Automatisme'
        },
        probleme: {
          patterns: ['test', 'specifique', 'probleme', 'resolution'],
          exclude: ['score', /q\d+/i],
          outputName: 'Probleme',
          displayName: 'Niveau de maîtrise - Résolution de problèmes'
        },
        espaceGeometrie: {
          patterns: ['espace', 'geometrie'],
          exclude: ['score', /q\d+/i],
          outputName: 'EEG',
          displayName: 'Espace et Géométrie'
        },
        grandeursMesures: {
          patterns: ['grandeur', 'mesure'],
          exclude: ['score', /q\d+/i],
          outputName: 'GEM',
          displayName: 'Grandeurs et Mesures'
        },
        nombresCalculs: {
          patterns: ['nombre', 'calcul'],
          exclude: ['score', /q\d+/i],
          outputName: 'NEC',
          displayName: 'Nombres et Calculs'
        },
        // Colonne supplémentaire pour la 4ème
        orgGestionDonnees: {
          patterns: ['organisation', 'gestion', 'fonctions'], // OR entre les paires
          exclude: ['score', /q\d+/i],
          outputName: 'OGD',
          displayName: 'Organisation et Gestion de Données'
        }
      },
      expectedQuestions: {
        automatismes: { count: 22, displayName: 'Automatismes' },
        problemes: { count: 19, displayName: 'Problèmes' }
      }
    }
  },

Français: {
    "6": {
      columns: {
        comprehensionOrale: {
          patterns: ['comprehension', 'oral'],
          exclude: ['score', /q\d+/i],
          outputName: 'ComprehensionOrale',
          displayName: "Compréhension de l'oral"
        },
        comprehensionEcrite: {
          patterns: ['test', 'specifique', 'comprehension', 'ecrit'],
          exclude: ['score', /q\d+/i],
          outputName: 'TestComprehensionEcrite',
          displayName: "Test spécifique de compréhension de l'écrit"
        },
        etudeLangueComposition: {
          patterns: ['etude', 'langue', 'phrase'],
          exclude: ['lexique', 'orthographe', 'score', /q\d+/i],
          outputName: 'EtudeLangueComposition',
          displayName: 'Étude de la langue - Composition'
        },
        orthographe: {
          patterns: ['etude', 'langue', 'orthographe'],
          exclude: ['score', /q\d+/i],
          outputName: 'Orthographe',
          displayName: 'Étude de la langue - Orthographe'
        },
        lexique: {
          patterns: ['test', 'specifique', 'etude', 'langue', 'lexique'],
          exclude: ['score', /q\d+/i],
          outputName: 'TestLexique',
          displayName: 'Test spécifique en étude de la langue - Lexique'
        },
        fluence: {
          patterns: ['fluence'],
          exclude: ['score', /q\d+/i],
          outputName: 'Fluence',
          displayName: 'Fluence'
        }
      },
      expectedQuestions: {
        lexique: { count: 15, displayName: 'Lexique' },
        arachne: { count: 10, displayName: 'Arachné' },
        sommeil: { count: 9, displayName: 'Sommeil' }
      }
    },

    "5": {
      columns: {
        competencesLangagieres: {
          patterns: ['competences', 'langagieres', 'linguistiques'],
          exclude: ['score', /q\d+/i],
          outputName: 'CompetencesLangagieres',
          displayName: 'Compétences langagières et linguistiques'
        },
        fluence: {
          patterns: ['fluence'],
          exclude: ['score', /q\d+/i],
          outputName: 'Fluence',
          displayName: 'Fluence'
        }
      },
      expectedQuestions: {
        // Rangé sous « Compétence » (ce que lit l'affichage, et les imports passés) ; la plateforme écrit
        // « Compétences langagières et linguistiques Q01 - score » (export 2026).
        competences: { count: 24, displayName: 'Compétence', entete: 'Compétences langagières et linguistiques' }
      }
    },

    "4": {
      columns: {
        comprehensionOrale: {
          patterns: ['comprehension', 'oral'],
          exclude: ['score', /q\d+/i],
          outputName: 'ComprehensionOrale',
          displayName: "Compréhension de l'oral"
        },
        comprehensionEcrite: {
          patterns: ['test', 'specifique', 'comprehension', 'ecrit'],
          exclude: ['score', /q\d+/i],
          outputName: 'TestComprehensionEcrite',
          displayName: "Test spécifique de compréhension de l'écrit"
        },
        etudeLangueComposition: {
          patterns: ['etude', 'langue', 'phrase'],
          exclude: ['lexique', 'orthographe', 'score', /q\d+/i],
          outputName: 'EtudeLangueComposition',
          displayName: 'Étude de la langue - Composition et cohérence'
        },
        orthographe: {
          patterns: ['etude', 'langue', 'orthographe'],
          exclude: ['score', /q\d+/i],
          outputName: 'Orthographe',
          displayName: 'Étude de la langue - Orthographe'
        },
        lexique: {
          patterns: ['test', 'specifique', 'etude', 'langue', 'lexique'],
          exclude: ['score', /q\d+/i],
          outputName: 'TestLexique',
          displayName: 'Test spécifique en étude de la langue - Lexique'
        },
        fluence: {
          patterns: ['fluence'],
          exclude: ['score', /q\d+/i],
          outputName: 'Fluence',
          displayName: 'Fluence'
        }
      },
      expectedQuestions: {
        // Année 2026 : Petit Chose et Volcans sous la glace (Gaspillage était le texte des années passées).
        lexique: { count: 15, displayName: 'Lexique' },
        petitChose: { count: 10, displayName: 'Petit Chose' },
        volcans: { count: 11, displayName: 'Volcans sous la glace' }
      }
    }
  }
}